import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

const cssPrefix = 'GenerativeAIOnPremSection';
export namespace GenerativeAIOnPremSectionStyles {

    // Authoring section styles
    export const authoringContainer = cssClass(cssPrefix, 'authoring-container', {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: `${TabStyles.Spacing.ExtraLargeSpace}px`,
        marginBottom: '0.75em'
    });

    export const authoringIconLabel = cssClass(cssPrefix, 'authoring-icon-label', {
        display: 'flex',
        alignItems: 'center',
        gap: `${TabStyles.Spacing.DefaultSpace + 2}px`
    });

    export const authoringIconDisabled = cssClass(cssPrefix, 'authoring-icon-disabled', {
        color: applyColor(DesignTokens.color.font.default.disabled, TabStyles.Typography.Color.Disabled)
    });

    // Info icon + Turn On/Off button grouped on the right of a feature row.
    export const featureButtonContainer = cssClass(cssPrefix, 'feature-button-container', {
        display: 'flex',
        alignItems: 'center',
        gap: `${TabStyles.Spacing.DefaultSpace + 2}px`
    });

    // Dynamic styles functions
    export const getAuthoringLabelStyle = (isEnabled: boolean) => ({
        color: isEnabled ? 'inherit' : applyColor(DesignTokens.color.font.default.disabled, TabStyles.Typography.Color.Disabled)
    });

    // API Key action section styles
    export const apiKeyActionContainer = cssClass(cssPrefix, 'api-key-action-container', {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    });

    export const apiKeyButtonsContainer = cssClass(cssPrefix, 'api-key-buttons-container', {
        display: 'flex',
        gap: TabStyles.Spacing.MediumSpace
    });

    export const apiKeyActionBackground = cssClass(cssPrefix, 'api-key-action-background', {
        backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, TabStyles.Colors.L2),
        padding: TabStyles.Spacing.LargeSpace - 2
    });

    // Alert message styles
    export const alertContainer = cssClass(cssPrefix, 'alert-container', {
        marginBottom: TabStyles.Spacing.LargeSpace,
        marginTop: TabStyles.Spacing.LargeSpace
    });

    // Section label with margin
    export const sectionLabel = cssClass(cssPrefix, 'section-label', {
        marginTop: TabStyles.Spacing.ExtraLargeSpace
    });

    // Error details modal styles
    // Dimensions below come from the Figma design (Tableau AI Settings, node 3618:25868).
    // No exact spacing tokens exist for 8 or 16, so those are expressed as raw pixels.
    export const ERROR_DETAILS_MODAL_HEIGHT = 400;
    const ERROR_DETAILS_MODAL_SCROLL_MIN_HEIGHT = 200;
    const ERROR_DETAILS_MODAL_BODY_GAP = 16;
    const ERROR_DETAILS_MODAL_SCROLL_PADDING = 8;
    const ERROR_DETAILS_MODAL_LINE_HEIGHT = 18;

    export const errorDetailsModalFooter = cssClass(cssPrefix, 'error-details-modal-footer', {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: TabStyles.Spacing.MediumSpace,
        padding: `${TabStyles.Spacing.MediumSpace}px ${TabStyles.Spacing.LargeSpace}px`
    });

    export const errorDetailsModalBody = cssClass(cssPrefix, 'error-details-modal-body', {
        display: 'flex',
        flexDirection: 'column',
        gap: ERROR_DETAILS_MODAL_BODY_GAP,
        height: '100%'
    });

    export const errorDetailsModalMessage = cssClass(cssPrefix, 'error-details-modal-message', {
        fontSize: TabStyles.Typography.Sizing.Default,
        lineHeight: `${ERROR_DETAILS_MODAL_LINE_HEIGHT}px`,
        letterSpacing: '0.2px'
    });

    export const errorDetailsModalLabel = cssClass(cssPrefix, 'error-details-modal-label', {
        fontSize: TabStyles.Typography.Sizing.Default,
        lineHeight: `${TabStyles.Typography.Sizing.Default}px`,
        letterSpacing: '0.2px'
    });

    export const errorDetailsModalScrollArea = cssClass(cssPrefix, 'error-details-modal-scroll-area', {
        flex: '1 1 0',
        minHeight: ERROR_DETAILS_MODAL_SCROLL_MIN_HEIGHT,
        border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.default, TabStyles.Colors.L3)}`,
        borderRadius: '1px',
        padding: ERROR_DETAILS_MODAL_SCROLL_PADDING,
        overflow: 'auto',
        boxSizing: 'border-box',
        height: 0
    });

    export const errorDetailsModalText = cssClass(cssPrefix, 'error-details-modal-text', {
        fontSize: TabStyles.Typography.Sizing.Default,
        lineHeight: `${ERROR_DETAILS_MODAL_LINE_HEIGHT}px`,
        letterSpacing: '0.2px',
        fontFamily: 'ui-monospace, "SFMono-Regular", "Cascadia Mono", "Menlo", monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    });

}
